import type { ChatAttachment, ChatComposerMemoryFallback } from "../lib/chat/chat-types.ts";
import { releaseChatAttachmentPayloads } from "../pages/chat/attachment-payload-store.ts";
import type { ApplicationChatAttachmentHandoff } from "./context.ts";

const MAX_PENDING_CHAT_ATTACHMENT_ENTRIES = 32;
// Hidden split panes can remain unmounted indefinitely, so wall-clock expiry
// would lose valid drafts. Bounded oldest-first eviction owns abandoned cleanup.

type PendingChatAttachmentHandoff = {
  owner: NonNullable<Parameters<ApplicationChatAttachmentHandoff["prepare"]>[0]["owner"]>;
  scopeKey: string;
  attachments: ChatAttachment[];
  fallbacks: Record<string, ChatComposerMemoryFallback>;
};

export function createChatAttachmentHandoff(): ApplicationChatAttachmentHandoff {
  const pending = new Map<string, PendingChatAttachmentHandoff>();
  let disposed = false;

  const release = (attachments: readonly ChatAttachment[] = []) =>
    releaseChatAttachmentPayloads(attachments);
  const handoffAttachments = (handoff: PendingChatAttachmentHandoff) => {
    const byId = new Map(handoff.attachments.map((attachment) => [attachment.id, attachment]));
    for (const fallback of Object.values(handoff.fallbacks)) {
      for (const attachment of fallback.attachments) {
        byId.set(attachment.id, attachment);
      }
    }
    return [...byId.values()];
  };
  const releaseHandoff = (
    handoff: PendingChatAttachmentHandoff | undefined,
    retainedIds = new Set<string>(),
  ) => {
    if (!handoff) {
      return;
    }
    release(handoffAttachments(handoff).filter((attachment) => !retainedIds.has(attachment.id)));
  };
  const take = (paneId: string) => {
    const handoff = pending.get(paneId);
    if (handoff) {
      pending.delete(paneId);
    }
    return handoff;
  };

  return {
    prepare: ({ owner, paneId, scopeKey, attachments, fallbacks }) => {
      const previous = take(paneId);
      const fallbackEntries = Object.entries(fallbacks);
      if (attachments.length === 0 && fallbackEntries.length === 0) {
        releaseHandoff(previous);
        return;
      }
      const retainedIds = new Set(attachments.map((attachment) => attachment.id));
      for (const fallback of Object.values(fallbacks)) {
        for (const attachment of fallback.attachments) {
          retainedIds.add(attachment.id);
        }
      }
      releaseHandoff(previous, retainedIds);
      if (!owner || disposed) {
        release(attachments);
        for (const fallback of Object.values(fallbacks)) {
          release(fallback.attachments);
        }
        return;
      }
      pending.set(paneId, {
        owner,
        scopeKey,
        attachments: [...attachments],
        fallbacks: Object.fromEntries(
          fallbackEntries.map(([key, fallback]) => [
            key,
            { ...fallback, attachments: [...fallback.attachments] },
          ]),
        ),
      });
      // Route handoffs normally consume immediately. Bounds make abandoned
      // split panes release their packages instead of leaking for the tab lifetime.
      for (const oldestPaneId of pending.keys()) {
        if (pending.size <= MAX_PENDING_CHAT_ATTACHMENT_ENTRIES) {
          break;
        }
        releaseHandoff(take(oldestPaneId));
      }
    },
    consume: ({ owner, paneId, scopeKey }) => {
      const match = take(paneId);
      // Reusing a pane id with another session or Gateway is terminal for the
      // old owner; keeping it would allow a later remount to recover stale evidence.
      if (match?.owner === owner && match.scopeKey === scopeKey) {
        return { attachments: match.attachments, fallbacks: match.fallbacks };
      }
      releaseHandoff(match);
      return null;
    },
    clearPane: (paneId) => releaseHandoff(take(paneId)),
    dispose: () => {
      disposed = true;
      for (const handoff of pending.values()) {
        releaseHandoff(handoff);
      }
      pending.clear();
    },
  };
}
